import { type SeoCluster } from "@shared/config/seo-pages";
import {
  Download,
  FileAudio,
  FileText,
  Gauge,
  Globe,
  Languages,
  Link2,
  ListOrdered,
  type LucideIcon,
  Mic,
  Scale,
  Shield,
  Sparkles,
  Timer,
  Users,
  Video,
  Wand2,
  Zap
} from "lucide-react";

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface FeaturesVariant {
  heading: string;
  subtitle: string;
  features: Feature[];
}

/**
 * Блок «Возможности» разный для каждой тематической группы.
 * Раньше он был одинаковым на всех посадочных и составлял заметную долю
 * текста страницы, из-за чего страницы выглядели похожими для поисковиков.
 */
const DEFAULT_VARIANT: FeaturesVariant = {
  heading: "Всё необходимое для транскрибации",
  subtitle: "Профессиональные функции для журналистов, исследователей, бизнеса и создателей контента.",
  features: [
    {
      icon: Zap,
      title: "Быстрая обработка",
      description: "Расшифровка занимает 10–15 % от длительности записи: часовой файл готов примерно за 7 минут."
    },
    {
      icon: Shield,
      title: "Высокая точность",
      description: "До 95–99 % на записях хорошего качества — достаточно, чтобы текст можно было использовать в работе."
    },
    {
      icon: Globe,
      title: "Многоязычное распознавание",
      description:
        "Русский, английский, языки Европы и Азии, а также казахский, узбекский, армянский и грузинский. Есть автоопределение языка."
    },
    {
      icon: Download,
      title: "Четыре формата выгрузки",
      description: "TXT, DOCX, PDF и SRT. Формат выбирается после обработки — переделывать ничего не нужно."
    },
    {
      icon: Users,
      title: "Разделение по спикерам",
      description: "Реплики сгруппированы по говорящим — в диалогах и созвонах видно, кто что сказал."
    },
    {
      icon: Sparkles,
      title: "AI-сводка",
      description: "Краткое изложение и ключевые моменты, когда читать полную расшифровку некому."
    }
  ]
};

const CLUSTER_VARIANTS: Record<SeoCluster, FeaturesVariant> = {
  format: {
    heading: "Любой формат — без конвертации",
    subtitle: "Сервис сам разбирается с контейнером и достаёт из него звук.",
    features: [
      {
        icon: FileAudio,
        title: "Формат определяется сам",
        description: "Расширение файла не нужно указывать вручную — сервис распознаёт контейнер автоматически."
      },
      {
        icon: Video,
        title: "Звук из видео",
        description:
          "Аудиодорожка извлекается из видеофайла автоматически, изображение при расшифровке не используется."
      },
      {
        icon: Gauge,
        title: "До 2 ГБ на файл",
        description:
          "Лимит покрывает большинство записей. Более длинные нужно разбить на части — до четырёх часов каждая."
      },
      {
        icon: Shield,
        title: "Точность не зависит от формата",
        description: "На результат влияет качество записи, а не расширение. Разница между WAV и хорошим MP3 минимальна."
      },
      {
        icon: Users,
        title: "Разделение по спикерам",
        description: "Работает одинаково для всех форматов — реплики группируются по говорящим."
      },
      {
        icon: Download,
        title: "Выгрузка в TXT, DOCX, PDF и SRT",
        description: "Один раз расшифровали — забираете в любом формате, в том числе в нескольких сразу."
      }
    ]
  },
  language: {
    heading: "Много языков, включая редкие",
    subtitle: "Языки Кавказа и Центральной Азии, для которых готовых инструментов почти нет.",
    features: [
      {
        icon: Languages,
        title: "Языки Европы, Азии и СНГ",
        description:
          "Русский, английский, европейские и азиатские языки, а также казахский, узбекский, армянский и грузинский."
      },
      {
        icon: Wand2,
        title: "Автоопределение языка",
        description: "Если язык записи заранее неизвестен, модель определит его сама по первым секундам."
      },
      {
        icon: FileText,
        title: "Пунктуация и заглавные буквы",
        description: "Текст приходит оформленным — его можно читать сразу, не тратя время на расстановку знаков."
      },
      {
        icon: Shield,
        title: "Устойчивость к акцентам",
        description: "Модель обучена на разнообразной речи, поэтому акцент снижает точность незначительно."
      },
      {
        icon: Gauge,
        title: "Два режима обработки",
        description: "Быстрый — для чистых записей, «Качество» — для сложного звука и редких языков."
      },
      {
        icon: Users,
        title: "Спикеры и таймкоды",
        description: "Разделение по говорящим и привязка ко времени работают на любом из поддерживаемых языков."
      }
    ]
  },
  platform: {
    heading: "Ссылкой или файлом",
    subtitle: "Открытые публикации — по ссылке, записи созвонов — обычной загрузкой.",
    features: [
      {
        icon: Link2,
        title: "Расшифровка по ссылке",
        description: "Для открытых роликов на YouTube, VK Video и Rutube скачивать ничего не нужно."
      },
      {
        icon: Video,
        title: "Записи созвонов как есть",
        description: "MP4 или M4A из Zoom, Meet, Teams и Телемоста загружаются без конвертации."
      },
      {
        icon: Users,
        title: "Кто что сказал",
        description: "Разделение по спикерам разводит реплики участников — главное на любой групповой встрече."
      },
      {
        icon: Timer,
        title: "Таймкоды по всей записи",
        description: "От фразы в тексте можно сразу перейти к нужной минуте исходного файла."
      },
      {
        icon: Sparkles,
        title: "Протокол из сводки",
        description: "AI-сводка собирает из часового обсуждения краткое изложение и ключевые моменты."
      },
      {
        icon: Gauge,
        title: "Длинные встречи",
        description: "До четырёх часов и 2 ГБ на файл — этого хватает практически на любой созвон."
      }
    ]
  },
  usecase: {
    heading: "Под конкретную задачу",
    subtitle: "Совещание, интервью или исследование — у каждого сценария свои требования к тексту.",
    features: [
      {
        icon: Users,
        title: "Разделение по спикерам",
        description: "Для интервью, созвонов и групповых обсуждений — самая трудоёмкая часть ручной расшифровки."
      },
      {
        icon: Timer,
        title: "Ссылки на моменты записи",
        description: "Таймкоды позволяют сослаться на конкретную минуту, а не на «где-то в середине»."
      },
      {
        icon: Sparkles,
        title: "Сводка вместо десяти страниц",
        description: "Краткое изложение и ключевые моменты, когда полный текст читать некому."
      },
      {
        icon: FileText,
        title: "Документ для правки",
        description: "Выгрузка в DOCX даёт готовый файл с абзацами и разметкой — остаётся вычитать."
      },
      {
        icon: Mic,
        title: "Работает с неидеальным звуком",
        description: "Диктофон, телефон, шумная переговорка — модель устойчива к фоновому шуму."
      },
      {
        icon: Scale,
        title: "Вычитка там, где важно",
        description: "Для цитат и юридических формулировок текст стоит сверять с записью — таймкоды это упрощают."
      }
    ]
  },
  export: {
    heading: "Формат выбирается после расшифровки",
    subtitle: "Обработка делается один раз, а забрать результат можно в любом виде.",
    features: [
      {
        icon: FileText,
        title: "DOCX для правки",
        description: "Открывается в Word, «Google Документах», LibreOffice и «МойОфис» без конвертации."
      },
      {
        icon: Download,
        title: "PDF для передачи",
        description: "Вёрстка фиксируется — документ выглядит одинаково у всех, кому вы его отправите."
      },
      {
        icon: ListOrdered,
        title: "SRT для субтитров",
        description: "Готовые тайминги — файл подключается к видео как дорожка субтитров."
      },
      {
        icon: Timer,
        title: "Таймкоды во всех форматах",
        description: "Привязка реплик ко времени сохраняется при выгрузке, кроме чистого TXT."
      },
      {
        icon: Users,
        title: "Спикеры сохраняются",
        description: "Группировка реплик по говорящим переносится в документ, а не теряется при экспорте."
      },
      {
        icon: Sparkles,
        title: "AI-сводка рядом с текстом",
        description: "Краткое изложение не заменяет расшифровку, а идёт вместе с ней."
      }
    ]
  }
};

export const getFeaturesVariant = (cluster?: SeoCluster): FeaturesVariant =>
  cluster ? CLUSTER_VARIANTS[cluster] : DEFAULT_VARIANT;
